import {
  FocusEvent,
  ForwardedRef,
  MouseEvent as ReactMouseEvent,
  useId,
  useRef,
  useState,
} from 'react';

import {useComposedRef} from 'shared/web';

import {DEFAULT_TOOLBAR} from '../constants/defaultToolbar';
import {RichTextProps} from '../types';
import {buildToolbarGroups} from '../utils/buildToolbarGroups';
import {getCurrentSelectionRange} from '../utils/selectionHelpers';
import {useEditorEmoji} from './useEditorEmoji';
import {useEditorFormat} from './useEditorFormat';
import {useEditorKeyboard} from './useEditorKeyboard';
import {useEditorSelection} from './useEditorSelection';
import {useEditorValue} from './useEditorValue';

export function useRichTextEditor(props: RichTextProps, userRef: ForwardedRef<HTMLDivElement>) {
  const id = useId();
  const helperId = `${id}-helper`;
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const ref = useComposedRef(editorRef, userRef);

  const [isFocused, setIsFocused] = useState(false);

  const toolbarGroups = buildToolbarGroups(props.toolbar ?? DEFAULT_TOOLBAR);

  const {selectionRef, selectionPath, activeCommands, selectedFormat, updateMetadata} =
    useEditorSelection(editorRef);

  const {
    currentValue,
    currentLength,
    isOverLimit,
    isNearLimit,
    isEmpty,
    handleInput,
    handlePaste,
    syncEditorState,
  } = useEditorValue(props, editorRef, selectionRef, updateMetadata);

  const {handleKeyDown, handleToolbarKeyDown} = useEditorKeyboard(
    props,
    editorRef,
    toolbarRef,
    selectionRef,
    syncEditorState,
    updateMetadata
  );

  const {handleCommandClick, handleFormatChange} = useEditorFormat(
    editorRef,
    selectionRef,
    syncEditorState,
    updateMetadata
  );

  const {
    pickerRef,
    isEmojiPickerOpen,
    setIsEmojiPickerOpen,
    activeEmojiCategory,
    setActiveEmojiCategory,
    handleEmojiInsert,
  } = useEditorEmoji(editorRef, selectionRef, syncEditorState, updateMetadata);

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    setIsFocused(true);
    selectionRef.current = getCurrentSelectionRange(editorRef.current);
    updateMetadata();
    props.onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const currentRange = getCurrentSelectionRange(editorRef.current);
    if (currentRange) {
      selectionRef.current = currentRange;
    }

    // Don't trigger blur when focus moves to the toolbar (same composite widget).
    // This prevents the field from being marked "touched" while interacting
    // with formatting controls.
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && toolbarRef.current?.contains(relatedTarget)) {
      return;
    }

    setIsFocused(false);
    props.onBlur?.(event);
  };

  // Called from the format dropdown's onMouseDown — fires before the editor loses focus,
  // ensuring selectionRef holds the correct cursor position when the format is applied.
  const captureSelectionBeforeBlur = () => {
    const range = getCurrentSelectionRange(editorRef.current);
    if (range) {
      selectionRef.current = range;
    }
  };

  return {
    id,
    helperId,
    ref,
    editorRef,
    toolbarRef,
    pickerRef,
    currentValue,
    selectionPath,
    activeCommands,
    selectedFormat,
    isFocused,
    isEmojiPickerOpen,
    activeEmojiCategory,
    setActiveEmojiCategory,
    setIsEmojiPickerOpen,
    toolbarGroups,
    currentLength,
    isOverLimit,
    isNearLimit,
    isEmpty,
    handleInput,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handlePaste,
    handleToolbarKeyDown,
    handleCommandClick,
    handleFormatChange,
    handleEmojiInsert,
    preventToolbarBlur,
    captureSelectionBeforeBlur,
  };
}

function preventToolbarBlur(event: ReactMouseEvent<HTMLElement>) {
  event.preventDefault();
}
